import { GDFPalletCode } from '../gdf/type';
import { WeatherSettingsChangeSource } from '../settings/settings';
import { isSigWx } from '../utils/utils';

export interface LayerReloadInfo {
    reloadLayers: boolean;
    reloadData: boolean;
}

const NO_RELOAD: LayerReloadInfo = {
    reloadLayers: false,
    reloadData: false
} as const;

const RELOAD: LayerReloadInfo = {
    reloadLayers: true,
    reloadData: true
} as const;

// This weather change source to GDF palettes that are affected by the settings change.
// This exists because most of the settings only affect a subset of layers. The reason we map change source to palette code is because
// it's not always known at ahead of time what palette a given product will use.
// There's two types of settings changes, those that affect only change how the data is displayed and those (e.g. filter) that affect what data
// is returned from the weather store when the layer requests data.
// If reloadData is true the layer should request new data from the weather store.
// If reloadLayers is true, the layer updates it's maplibre/custom layers.
const changeSourceToAffectedPalettes: Partial<Record<WeatherSettingsChangeSource, [GDFPalletCode[], LayerReloadInfo]>> = {
    [WeatherSettingsChangeSource.all]: [[], { reloadData: true, reloadLayers: true }],
    [WeatherSettingsChangeSource.unknown]: [[], { reloadData: true, reloadLayers: true }],
    // Metar and Taf use the same palette
    [WeatherSettingsChangeSource.airportMetarTafDisplay]: [[GDFPalletCode.metar], { reloadData: false, reloadLayers: true }],
    [WeatherSettingsChangeSource.airportMetarTafFilter]: [[GDFPalletCode.metar], { reloadData: true, reloadLayers: true }],
    [WeatherSettingsChangeSource.airportMyAirports]: [
        [GDFPalletCode.metar, GDFPalletCode.delay, GDFPalletCode.rvr],
        { reloadData: true, reloadLayers: false }
    ],
    [WeatherSettingsChangeSource.airportDelays]: [[GDFPalletCode.delay], { reloadData: false, reloadLayers: true }],
    [WeatherSettingsChangeSource.airportRvr]: [[GDFPalletCode.rvr], { reloadData: false, reloadLayers: true }],
    [WeatherSettingsChangeSource.airportFilterGroup]: [
        [GDFPalletCode.metar, GDFPalletCode.delay, GDFPalletCode.rvr, GDFPalletCode.diversion],
        { reloadData: true, reloadLayers: false }
    ],
    [WeatherSettingsChangeSource.airmetsSigmets]: [
        [GDFPalletCode.airmets, GDFPalletCode.sigmets, GDFPalletCode.csigmets],
        { reloadData: true, reloadLayers: false }
    ],
    [WeatherSettingsChangeSource.airspaceFcaFea]: [[GDFPalletCode.fca], { reloadData: true, reloadLayers: false }],
    [WeatherSettingsChangeSource.dsua]: [[GDFPalletCode.dsua], { reloadData: true, reloadLayers: false }],
    [WeatherSettingsChangeSource.radarSummary]: [[GDFPalletCode.radsum], { reloadData: false, reloadLayers: true }],
    [WeatherSettingsChangeSource.radarSummaryStyle]: [
        [GDFPalletCode.radsum, GDFPalletCode.probsevere],
        { reloadData: true, reloadLayers: true }
    ],
    [WeatherSettingsChangeSource.windsBarbsColor]: [[GDFPalletCode.metar], { reloadData: true, reloadLayers: true }],
    [WeatherSettingsChangeSource.probSevere]: [[GDFPalletCode.probsevere], { reloadData: true, reloadLayers: true }]
};

export function changeSourceAffectsProduct(
    productId: number,
    palette: GDFPalletCode,
    changeSource: WeatherSettingsChangeSource
): LayerReloadInfo {
    if (changeSource === WeatherSettingsChangeSource.airspacePireps) {
        // Pirep needs to be checked by product id because palette code is NONE
        if (productId === 0x600c || productId === 0x6086 || productId === 0x71) {
            return {
                reloadLayers: false,
                reloadData: true
            };
        }
    }
    if (changeSource === WeatherSettingsChangeSource.windsBarbsColor) {
        // Pirep needs to be checked by product id because palette code is NONE
        if (productId === 0x600c || productId === 0x6086 || productId === 0x71) {
            return {
                reloadLayers: true,
                reloadData: false
            };
        }
    }

    // Sig wx or Jet streams
    if (isSigWx(productId) || productId === 0x8580) {
        if (
            changeSource === WeatherSettingsChangeSource.jetStreamWidth ||
            changeSource === WeatherSettingsChangeSource.windsBarbsColor ||
            changeSource === WeatherSettingsChangeSource.sigWx
        ) {
            return {
                reloadData: false,
                reloadLayers: true
            };
        }
    }

    if (
        changeSource === WeatherSettingsChangeSource.turbulenceIata ||
        changeSource === WeatherSettingsChangeSource.turbulenceTaps ||
        changeSource === WeatherSettingsChangeSource.windsBarbsColor
    ) {
        // Taps
        if (productId === 0x601e) {
            return {
                reloadData: true,
                reloadLayers: true
            };
        }
    }

    if (changeSource === WeatherSettingsChangeSource.radarSummary) {
        if (productId === 0x0082) {
            return { reloadData: true, reloadLayers: true };
        }
    }

    if (changeSource === WeatherSettingsChangeSource.radarSummaryStyle) {
        if (productId === 0x0082) {
            return { reloadData: false, reloadLayers: true };
        }
    }

    if (changeSource === WeatherSettingsChangeSource.minimumAshCloudConcentration) {
        if (productId >= 0x604c && productId <= 0x604f) {
            return {
                reloadData: true,
                reloadLayers: false
            };
        }
    }

    const affectedPalette = changeSourceToAffectedPalettes[changeSource];
    if (!affectedPalette) {
        return NO_RELOAD;
    }

    if (affectedPalette[0].includes(palette)) {
        return affectedPalette[1];
    }

    return NO_RELOAD;
}
